<template>
  <span>
    <v-card class="mb-4 pa-4" elevation="0">
      <template v-if="loadingRanking">
        <div class="d-flex flex-column align-center justify-center py-10">
          <v-progress-circular indeterminate color="primary" size="48" />
          <div class="mt-4 grey--text text--darken-1">
            Carregando ranking...
          </div>
        </div>
      </template>

      <template v-else>
        <v-card-title class="pa-0 mb-1">
          <v-icon left small color="primary">mdi-chart-bar</v-icon>
          Ranking ISFP por Empresa
        </v-card-title>

        <v-card-subtitle class="pa-0 mb-3 mt-1">
          Clique em uma barra para ver os serviços com maior PSFP da empresa
        </v-card-subtitle>

        <highcharts
          v-if="rankingEmpresas.length > 0"
          :options="opcoesRanking"
          style="width: 100%"
        />

        <v-row v-else justify="center">
          <v-col cols="auto" class="text-center grey--text py-10">
            <v-icon large color="grey lighten-1">mdi-chart-bar</v-icon>
            <div class="mt-2">Nenhum dado disponível</div>
          </v-col>
        </v-row>
      </template>
    </v-card>

    <v-card v-if="modoDrill" class="mb-4 pa-4" elevation="0">
      <template v-if="loadingDrill">
        <div class="d-flex flex-column align-center justify-center py-10">
          <v-progress-circular indeterminate color="primary" size="48" />
          <div class="mt-4 grey--text text--darken-1">
            Carregando detalhamento...
          </div>
        </div>
      </template>

      <template v-else>
        <v-card-title class="pa-0 mb-1">
          Top 10 Serviços por PSFP — {{ empresaDrillNome }}
        </v-card-title>

        <v-card-subtitle class="pa-0 mb-3 mt-1">
          Serviços com maior percentual fora do prazo na empresa selecionada
        </v-card-subtitle>

        <highcharts
          v-if="drillServicos.length > 0"
          :options="opcoesDrill"
          style="width: 100%"
        />

        <v-row v-else justify="center">
          <v-col cols="auto" class="text-center grey--text py-10">
            <v-icon large color="grey lighten-1">mdi-chart-bar</v-icon>
            <div class="mt-2">Nenhum dado disponível para esta empresa</div>
          </v-col>
        </v-row>
      </template>
    </v-card>
  </span>
</template>

<script>
import DashboardIsfpPsfpService from "@/services/DashboardIsfpPsfpService";
import dayjs from "dayjs";

export default {
  name: "DashboardIsfpPsfpVisaoBrasil",
  props: {
    activatedTab: { type: Number, required: true },
    competencia: { type: String, default: null },
    modoPeriodo: { type: String, default: "12m" },
    ano: { type: Number, default: null },
    reloadKey: {
      type: Number,
      deafult: 0,
    },
    filtros: {
      type: Object,
      default: () => ({
        codigoServico: null,
        tipoOutorga: opcao_todos,
        grupoEconomico: opcao_todos,
        uf: opcao_todos,
        regiao: opcao_todos,
      }),
    },
  },
  data: () => ({
    loadingRanking: false,
    loadingDrill: false,
    isFetching: false,
    rankingEmpresas: [],
    drillServicos: [],
    modoDrill: false,
    empresaDrillNome: "",
    empresaDrillCnpj: null,
  }),
  computed: {
    alturaGraficoDrill() {
      return Math.max(300, this.drillServicos.length * 48 + 100);
    },

    opcoesRanking() {
      const self = this;
      const quantidade = this.rankingEmpresas.length;
      const escapeHtmlAxisLabel = (s) =>
        String(s)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");

      return {
        chart: {
          type: "column",
          height: 340,
          style: { fontFamily: "inherit" },
          scrollablePlotArea: {
            minWidth: quantidade > 12 ? quantidade * 100 : null,
            scrollPositionX: 0,
          },
        },
        title: { text: null },
        credits: { enabled: false },
        xAxis: {
          categories: this.rankingEmpresas.map((e) => e.sigla || e.nome),
          crosshair: true,
          labels: {
            useHTML: true,
            style: { fontSize: "12px" },
            formatter() {
              const texto =
                this.value != null && this.value !== ""
                  ? String(this.value)
                  : "";
              return escapeHtmlAxisLabel(texto);
            },
          },
        },
        yAxis: {
          title: { text: null },
          labels: { format: "{value}%" },
          gridLineDashStyle: "Dash",
        },
        tooltip: {
          formatter() {
            const e = self.rankingEmpresas[this.point.index];
            return `<b>${e.nome}</b><br/>ISFP: <b>${this.y.toFixed(1)}%</b>`;
          },
        },
        legend: { enabled: false },
        plotOptions: {
          series: {
            animation: false,
            pointPadding: 0.05,
          },
          column: {
            borderRadius: 4,
            cursor: "pointer",
            pointPlacement: 0,
            point: {
              events: {
                click() {
                  const empresa = self.rankingEmpresas[this.index];
                  if (empresa) self.abrirDrill(empresa);
                },
              },
            },
            dataLabels: {
              enabled: true,
              format: "{y:.1f}%",
              style: {
                fontSize: "11px",
                fontWeight: "normal",
                color: "#555",
              },
            },
          },
        },
        series: [
          {
            name: "ISFP",
            data: this.rankingEmpresas.map((e) => e.isfp),
            type: "column",
            animation: false,
          },
        ],
        lang: {
          noData: "Sem dados para exibir",
        },
      };
    },

    opcoesDrill() {
      const cores = this.drillServicos.map((s) => {
        if (s.psfp <= 1) return "#4f9c3a";
        if (s.psfp <= 5) return "#8bc34a";
        if (s.psfp <= 10) return "#c7a800";
        if (s.psfp <= 20) return "#e07020";
        return "#c83232";
      });

      return {
        chart: {
          type: "bar",
          height: this.alturaGraficoDrill,
          style: { fontFamily: "inherit" },
        },
        title: { text: null },
        credits: { enabled: false },
        xAxis: {
          categories: this.drillServicos.map(
            (s) => `${s.indice_categoria} - ${s.descricao_por_indice}`,
          ),
        },
        yAxis: {
          title: { text: null },
          labels: { format: "{value}%" },
        },
        legend: { enabled: false },
        plotOptions: {
          bar: {
            borderRadius: 4,
            dataLabels: {
              enabled: true,
              format: "{y:.1f}%",
            },
          },
        },
        series: [
          {
            name: "PSFP",
            data: this.drillServicos.map((s, index) => ({
              y: s.psfp,
              color: cores[index],
            })),
          },
        ],
      };
    },
  },
  watch: {
    activatedTab: {
      immediate: true,
      handler(novoValor) {
        if (novoValor === 3) {
          this.voltarRanking();
          this.getDadosRanking();
        }
      },
    },

    reloadKey() {
      if (this.activatedTab === 3) {
        this.voltarRanking();
        this.getDadosRanking();
      }
    },
  },
  methods: {
    normalizarFiltro(valor) {
      return valor === opcao_todos ? null : valor;
    },

    normalizarCompetencia(competencia) {
      if (!competencia) return null;

      return dayjs(`${competencia}-01`)
        .endOf("month")
        .format("YYYY-MM-DD");
    },

    voltarRanking() {
      this.modoDrill = false;
      this.empresaDrillNome = "";
      this.empresaDrillCnpj = null;
      this.drillServicos = [];
    },

    async abrirDrill(empresa) {
      this.modoDrill = true;
      this.empresaDrillNome = empresa.nome;
      this.empresaDrillCnpj = empresa.cnpj;
      await this.getDadosDrill();
    },

    async getDadosRanking() {
      if (this.isFetching) return;

      this.$emit("updateLoadingFiltrosVisaoBrasil", true);
      this.isFetching = true;
      this.loadingRanking = true;
      this.rankingEmpresas = [];

      try {
        const competenciaNormalizada = this.normalizarCompetencia(
          this.competencia,
        );

        const params = {
          competencia: competenciaNormalizada,
          modoPeriodo: this.modoPeriodo,
          ano: this.modoPeriodo === "ano_civil" ? this.ano : null,
          codigoServico: this.filtros.codigoServico,
          tipoOutorga: this.filtros.tipoOutorga,
          grupoEconomico: this.filtros.grupoEconomico,
          uf: this.filtros.uf,
          regiao: this.filtros.regiao,
        };

        const response = await DashboardIsfpPsfpService.getDadosVisaoBrasilRanking(
          competenciaNormalizada,
          params,
        );

        this.rankingEmpresas = response?.ranking_empresas || [];
      } catch (error) {
        console.error("Erro ao carregar ranking da Visão Brasil:", error);
        this.rankingEmpresas = [];
      } finally {
        this.loadingRanking = false;
        this.isFetching = false;
        this.$emit("updateLoadingFiltrosVisaoBrasil", false);
      }
    },

    async getDadosDrill() {
      if (this.isFetching) return;

      this.isFetching = true;
      this.loadingDrill = true;
      this.$emit("updateLoadingFiltrosVisaoBrasil", true);
      this.drillServicos = [];

      try {
        const competenciaNormalizada = this.normalizarCompetencia(
          this.competencia,
        );

        const params = {
          competencia: competenciaNormalizada,
          modoPeriodo: this.modoPeriodo,
          ano: this.modoPeriodo === "ano_civil" ? this.ano : null,
          codigoServico: this.filtros.codigoServico,
        };

        const response = await DashboardIsfpPsfpService.getDadosVisaoBrasilDrillEmpresa(
          competenciaNormalizada,
          this.empresaDrillCnpj,
          params,
        );

        this.drillServicos = response?.top_servicos || [];
      } catch (error) {
        console.error("Erro ao carregar drill da Visão Brasil:", error);
        this.drillServicos = [];
      } finally {
        this.loadingDrill = false;
        this.isFetching = false;
        this.$emit("updateLoadingFiltrosVisaoBrasil", false);
      }
    },
  },
};
</script>
